export interface AppConfig {
  authority: string;
  clientId: string;
  projectId: string;
  /**
   * XTM Gateway app, rendered from MT_LLM_GATEWAY_URL at container start.
   * The env var keeps its old name; only the product was renamed.
   * Optional: the env var is optional, so an older or partially-rolled-out
   * config.json may not carry it. Absent, not just empty — readers that go
   * through getOptionalConfig() rather than buildToolLinks() must handle
   * `undefined`.
   */
  mtLlmGatewayUrl?: string;
}

let config: AppConfig | null = null;

export function buildScope(projectId: string): string {
  return [
    'openid',
    'profile',
    'email',
    'offline_access',
    `urn:zitadel:iam:org:project:id:${projectId}:aud`,
    'urn:zitadel:iam:org:projects:roles',
    // Reserved Zitadel scope: emits urn:zitadel:iam:user:resourceowner:id/:name/:primary_domain.
    // The backend requires the resourceowner id as a required identity claim.
    'urn:zitadel:iam:user:resourceowner',
  ].join(' ');
}

export async function loadConfig(): Promise<AppConfig> {
  const res = await fetch(`${import.meta.env.BASE_URL}config.json`);
  if (!res.ok) {
    throw new Error(`Failed to load config.json (status ${res.status})`);
  }
  config = (await res.json()) as AppConfig;
  return config;
}

export function getConfig(): AppConfig {
  if (!config) throw new Error('App config not loaded — call loadConfig() first');
  return config;
}

/**
 * Non-throwing accessor for readers that must tolerate an unloaded config —
 * the dev and msw auth modes render without a hard config requirement, unlike
 * the oidc path where getConfig()'s invariant protects the UserManager.
 */
export function getOptionalConfig(): AppConfig | null {
  return config;
}

/** Test-only: clear the cached config between cases. */
export function __resetConfigForTest(): void {
  config = null;
}
